<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>
      <ConfirmDialogue ref="confirmDialogue"/>

      <div class="mt-4 mb-5" style="max-width:800px">
        <h3>Synkroniser betalingsoppgjør</h3>

        <div v-show="!loading">
            <div v-if="locations.length === 0" class="mt-4">
              <span class="message-info">Ingen avdelinger har Tripletex aktivert. Aktiver det under Avdelinger.</span>
            </div>

            <div v-else-if="!configured" class="mt-4">
              <span class="message-info">Tripletex-konto er ikke konfigurert for denne avdelingen. Gå til <a href="/tripletex">Tripletex innstillinger</a> for å sette opp.</span>
            </div>

            <div v-if="configured">
                <div v-if="hasMultiLocations()" class="mt-4">
                  <span class="ingredient-title">Velg avdeling</span>
                  <select class="form-select" v-model="locationId" @change="onLocationChange()">
                    <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
                  </select>
                </div>

                <div class="mt-4">
                  <div class="mb-3 p-3 bg-body-tertiary rounded">
                    <p class="mb-2"><strong>Synkroniser betalingsoppgjør</strong> — Oppretter en <em>bankavstemming</em> i Tripletex basert på Z-rapporter. Kobler bankkonto (1920) med riktig regnskapsperiode.</p>
                    <p class="mb-2"><strong>Avstem mot bankutskrift</strong> — Henter banktransaksjoner fra Tripletex og sammenligner med Z-rapport betalingstotaler. Ingen data opprettes i Tripletex — kun lesing og sammenligning.</p>
                    <p class="mb-2"><strong>Søk bankavstemminger</strong> — Viser opprettede bankavstemminger i Tripletex for valgt periode. Du kan slette åpne avstemminger som er feilopprettet.</p>
                    <p class="mb-0 text-muted"><small><strong>Tips:</strong> I Tripletex GUI (Bank → Bankavstemming → Manuell) vises «Vedlegg»-fanen kun når det finnes en bankavstemming for perioden. Ingen «Vedlegg»-fane = ingen bankavstemming opprettet.</small></p>
                  </div>
                  <SelectDate ref="selectDateFrom" label="Velg fra og med dato"/>
                  <SelectDate ref="selectDateTo" label="Velg til og med dato"/>

                  <div class="d-flex gap-3 mt-2 flex-wrap">
                    <button type="button" class="bo_btn" @click="syncSettlements"> Synkroniser betalingsoppgjør </button>
                    <button type="button" class="bo_btn" @click="reconcileSettlements"> Avstem mot bankutskrift </button>
                    <button type="button" class="bo_btn" @click="searchReconciliations"> Søk bankavstemminger </button>
                  </div>

                  <div v-if="syncCompleted" class="mt-3">
                    <div :class="syncResult.success ? 'message-success' : 'message-error'">
                      <p>{{ syncResult.message }}</p>
                    </div>
                  </div>

                  <!-- Reconciliation results -->
                  <div v-if="reconcileCompleted" class="mt-4">
                    <div :class="reconcileResult.match ? 'message-success' : 'message-error'" class="mb-3">
                      <p><strong>{{ reconcileResult.message }}</strong></p>
                      <p class="mb-0">Z-rapporter: {{ reconcileResult.zReportCount }} | Banktransaksjoner: {{ reconcileResult.actual ? reconcileResult.actual.transactionCount : 0 }}</p>
                    </div>

                    <div v-if="!reconcileResult.bankDataAvailable" class="message-error mb-3">
                      <p>Kunne ikke hente bankdata fra Tripletex. Sjekk at bankintegrasjon er aktivert.</p>
                    </div>

                    <!-- Comparison table -->
                    <h5>Sammenligning</h5>
                    <table class="table table-bordered table-sm" v-if="reconcileResult.expected">
                      <thead class="table-group-divider">
                        <tr>
                          <th>Betalingstype</th>
                          <th class="text-end">Forventet (Z-rapport)</th>
                          <th class="text-end">Faktisk (bank)</th>
                          <th class="text-end">Differanse</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Kort</td>
                          <td class="text-end">{{ formatAmount(reconcileResult.expected.card) }}</td>
                          <td class="text-end">—</td>
                          <td class="text-end">—</td>
                          <td><span class="text-muted">Ikke splittet i bank</span></td>
                        </tr>
                        <tr>
                          <td>Kontant</td>
                          <td class="text-end">{{ formatAmount(reconcileResult.expected.cash) }}</td>
                          <td class="text-end">—</td>
                          <td class="text-end">—</td>
                          <td><span class="text-muted">Ikke splittet i bank</span></td>
                        </tr>
                        <tr>
                          <td>BankAxept</td>
                          <td class="text-end">{{ formatAmount(reconcileResult.expected.bankAxept) }}</td>
                          <td class="text-end">—</td>
                          <td class="text-end">—</td>
                          <td><span class="text-muted">Ikke splittet i bank</span></td>
                        </tr>
                        <tr>
                          <td>Vipps</td>
                          <td class="text-end">{{ formatAmount(reconcileResult.expected.vipps) }}</td>
                          <td class="text-end">—</td>
                          <td class="text-end">—</td>
                          <td><span class="text-muted">Ikke splittet i bank</span></td>
                        </tr>
                        <tr class="fw-bold">
                          <td>Total</td>
                          <td class="text-end">{{ formatAmount(reconcileResult.expected.total) }}</td>
                          <td class="text-end">{{ formatAmount(reconcileResult.actual ? reconcileResult.actual.bankTotal : 0) }}</td>
                          <td class="text-end" :class="reconcileResult.totalDifference !== 0 ? 'text-danger' : 'text-success'">
                            {{ formatAmount(reconcileResult.totalDifference) }}
                          </td>
                          <td>
                            <span v-if="reconcileResult.match" class="text-success fw-bold">OK</span>
                            <span v-else class="text-danger fw-bold">Avvik</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <!-- Bank transactions list -->
                    <div v-if="reconcileResult.actual && reconcileResult.actual.transactions && reconcileResult.actual.transactions.length > 0" class="mt-4">
                      <h5>Banktransaksjoner fra Tripletex</h5>
                      <table class="table table-bordered table-sm">
                        <thead class="table-group-divider">
                          <tr>
                            <th>Dato</th>
                            <th>Beskrivelse</th>
                            <th class="text-end">Beløp</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(tx, index) in reconcileResult.actual.transactions" :key="index">
                            <td>{{ tx.date }}</td>
                            <td>{{ tx.description }}</td>
                            <td class="text-end" :class="tx.amount >= 0 ? 'text-success' : 'text-danger'">{{ formatAmount(tx.amount) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Bank reconciliation search results -->
                  <div v-if="searchCompleted" class="mt-4">
                    <h5>Bankavstemminger i Tripletex</h5>
                    <div v-if="reconciliations.length === 0" class="message-info">
                      <p>{{ searchMessage }}</p>
                    </div>

                    <div v-else>
                      <p class="mb-2"><strong>{{ searchMessage }}</strong></p>
                      <table class="table table-sm table-bordered">
                        <thead class="table-group-divider">
                          <tr>
                            <th>ID</th>
                            <th>Type</th>
                            <th>Periode</th>
                            <th>Konto</th>
                            <th>Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(rec, index) in reconciliations" :key="index">
                            <td>{{ rec.id }}</td>
                            <td>{{ rec.type }}</td>
                            <td>{{ rec.periodStart }} — {{ rec.periodEnd }}</td>
                            <td>{{ rec.accountNumber }} {{ rec.accountName }}</td>
                            <td>
                              <span v-if="rec.isClosed" class="text-success">Lukket</span>
                              <span v-else class="text-muted">Åpen</span>
                            </td>
                            <td>
                              <button v-if="!rec.isClosed" type="button" class="btn btn-sm btn-outline-danger" @click="deleteSingleReconciliation(rec, index)">Slett</button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div v-if="deleteCompleted" class="mt-3">
                      <div :class="deleteResult.success ? 'message-success' : 'message-error'">
                        <p>{{ deleteResult.message }}</p>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
        </div>
      </div>
  </div>
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import SelectDate from "@/components/other/SelectDate";
  import ConfirmDialogue from "@/components/other/ConfirmDialogue";
  import SettingsService from "@/services/settings/SettingsService";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";

  export default {
    components: {LoadingSpinner, SelectDate, ConfirmDialogue},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        configured: false,
        syncCompleted: false,
        syncResult: {},
        reconcileCompleted: false,
        reconcileResult: {},
        searchCompleted: false,
        reconciliations: [],
        searchMessage: '',
        deleteCompleted: false,
        deleteResult: {}
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'TRIPLETEX');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.checkSyncStatus();
            } else {
              this.configured = false;
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      onLocationChange(){
        this.syncCompleted = false;
        this.reconcileCompleted = false;
        this.checkSyncStatus();
      },
      checkSyncStatus(){
        SettingsService.getTripletexSyncStatus(this.locationId).then(
            (response) => {
              if(response.data !== null){
                this.configured = response.data.configured;
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      syncSettlements(){
        this.loading = true;
        this.syncCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.syncPaymentSettlements(this.locationId, fromDate, toDate).then(
            (response) => {
              this.syncCompleted = true;
              this.syncResult = response.data;
              this.loading = false;
            },
            (error) => {
              this.syncCompleted = true;
              this.syncResult = { success: false, message: 'Feil ved synkronisering av betalingsoppgjør' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      reconcileSettlements(){
        this.loading = true;
        this.reconcileCompleted = false;
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.reconcilePaymentSettlements(this.locationId, fromDate, toDate).then(
            (response) => {
              this.reconcileCompleted = true;
              this.reconcileResult = response.data;
              this.loading = false;
            },
            (error) => {
              this.reconcileCompleted = true;
              this.reconcileResult = { match: false, message: 'Feil ved avstemming mot bankutskrift', bankDataAvailable: false };
              console.log(error);
              this.loading = false;
            }
        );
      },
      searchReconciliations(){
        this.loading = true;
        this.searchCompleted = false;
        this.deleteCompleted = false;
        this.reconciliations = [];
        let fromDate = this.$refs.selectDateFrom.getSelectedDate();
        let toDate = this.$refs.selectDateTo.getSelectedDate();

        SettingsService.searchBankReconciliations(this.locationId, fromDate, toDate).then(
            (response) => {
              this.searchCompleted = true;
              this.reconciliations = response.data.reconciliations || [];
              this.searchMessage = response.data.message || '';
              this.loading = false;
            },
            (error) => {
              this.searchCompleted = true;
              this.searchMessage = 'Feil ved søk etter bankavstemminger';
              console.log(error);
              this.loading = false;
            }
        );
      },
      async deleteSingleReconciliation(rec, index){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Slett bankavstemming',
          message: 'Er du sikker på at du vil slette bankavstemming #' + rec.id + ' (periode ' + rec.periodStart + ' — ' + rec.periodEnd + ')?',
          okButton: 'Slett',
          cancelButton: 'Avbryt'
        });
        if (!ok) return;
        this.loading = true;
        this.deleteCompleted = false;

        SettingsService.deleteBankReconciliation(this.locationId, rec.id).then(
            (response) => {
              if (response.data.success) {
                this.reconciliations.splice(index, 1);
                this.searchMessage = this.reconciliations.length + ' bankavstemming(er) funnet';
              } else {
                this.deleteCompleted = true;
                this.deleteResult = response.data;
              }
              this.loading = false;
            },
            (error) => {
              this.deleteCompleted = true;
              this.deleteResult = { success: false, message: 'Feil ved sletting av bankavstemming' };
              console.log(error);
              this.loading = false;
            }
        );
      },
      formatAmount(amount) {
        if (amount === null || amount === undefined) return '—';
        return new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' kr';
      }
    }
  };
</script>
